import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MapPin, Baby, Fence, Heart, ShieldCheck, GraduationCap, HeartPulse, Package, Handshake, BarChart3 } from "lucide-react";
import { BarChart, Bar as RBar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api, IMG } from "@/lib/api";
import { Reveal, SectionHead, CTAButton, SourceNote, Wave, Eyebrow } from "@/components/Shared";
import CircleOfProtection from "@/components/CircleOfProtection";
import ImpactGrid from "@/components/ImpactGrid";
import PondMap, { MapLegend } from "@/components/PondMap";

const HERO_STATS = [["4,000", "Estimated fatal unintentional drownings annually", "≈ 11 every day"], ["8,000", "Estimated nonfatal drownings annually", "≈ 22 every day"], ["#1", "Drowning: leading cause of death for ages 1–4", "U.S. children"], ["#2", "Second-leading unintentional injury death cause, ages 5–14", "Behind motor-vehicle crashes"]];
const BIG = [["Report a Pond", "/report", MapPin, "#EF4444", "Help identify potential risks in your community."], ["Sponsor a Child", "/sponsor?type=child", Baby, "#00B4D8", "Give a child the opportunity to learn to swim."], ["Sponsor a Fence", "/sponsor?type=fence", Fence, "#03045E", "Help protect a community's water's edge."], ["Donate", "/sponsor?type=general", Heart, "#FF6B35", "Fund every layer of protection."]];
export const PILLARS = [
  ["protect", "Protect", "One Pond at a Time™", ShieldCheck, "#0077B6", "Identify hazards, assess ponds, obtain authorization and install approved barriers."],
  ["teach", "Teach", "Rakim Water Safety Academy™", GraduationCap, "#00B4D8", "Swim lessons, water competency, pond safety and life-jacket use."],
  ["prepare", "Prepare", "Every Second Counts™", HeartPulse, "#FF6B35", "CPR awareness, first aid, safe rescue and water-watcher education."],
  ["equip", "Equip", "Rakim Safety Kit™", Package, "#EF4444", "Life jackets where appropriate, emergency cards and safety materials."],
  ["mobilize", "Mobilize", "Community Water Safety Alliance™", Handshake, "#10B981", "Fence companies, swim schools, churches, schools, responders and sponsors."],
  ["measure", "Measure", "Swim for Rakim Impact Center™", BarChart3, "#8B5CF6", "Show exactly what has been accomplished — publicly."],
];
const LAYERS = ["A fence is one layer.", "Education is another.", "Swimming ability is another.", "Supervision is another.", "Preparedness is another."];

function Hero() {
  return (
    <section className="sky-bg relative overflow-hidden" data-testid="home-hero">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 md:pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <Eyebrow>Protect • Educate • Prepare • Empower</Eyebrow>
          <h1 className="font-display uppercase font-bold text-5xl sm:text-6xl lg:text-7xl text-[#03045E] leading-[0.92] mt-4">Swim for<br /><span className="text-[#0077B6]">Rakim™</span></h1>
          <div className="brush inline-block mt-6 px-6 py-3"><p className="font-script text-3xl md:text-4xl text-white">Turning Tragedy Into Action.</p></div>
          <p className="mt-6 text-lg text-slate-700 max-w-xl">Protecting children through education, water competency and community safety — one child, one family, one pond and one community at a time.</p>
          <p className="mt-3 text-sm font-bold uppercase tracking-[0.15em] text-[#0096C7]">One Pond. One Fence. One Child. One Community. One Life at a Time.™</p>
        </div>
        <div className="lg:col-span-5 relative">
          <img src="/brand/logo.webp" alt="Swim for Rakim logo — a child splashing joyfully" className="w-full max-w-md mx-auto floaty drop-shadow-2xl" data-testid="hero-logo" />
          <p className="font-script text-2xl text-[#0077B6] text-center -mt-2 rotate-[-3deg]">His smile lives on through a safer, stronger, brighter tomorrow.</p>
        </div>
      </div>
      <Wave fill="#03045E" />
    </section>
  );
}

function StatsBand() {
  return (
    <section className="bg-[#03045E] relative grain" data-testid="home-stats">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <SectionHead light eyebrow="Drowning by the numbers" title="Real statistics. Real children. Real reasons to act." />
          <Link to="/data" className="text-sky-300 font-bold text-sm uppercase tracking-wider hover:text-white" data-testid="home-data-center-link">Open the Data Center →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {HERO_STATS.map(([v, l, s], i) => (
            <Reveal key={v} delay={i * 0.08}>
              <div className="rounded-2xl bg-white/5 border border-sky-400/20 p-5 md:p-6 h-full" data-testid={`hero-stat-${i}`}>
                <p className="font-display text-5xl md:text-6xl font-bold text-white">{v}</p>
                <p className="text-sky-100 text-sm mt-3 font-semibold">{l}</p>
                <p className="text-sky-300 text-xs mt-2 uppercase tracking-wider font-bold">{s}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {BIG.map(([l, to, Icon, c, d]) => (
            <Link key={l} to={to} data-testid={`home-cta-${l.toLowerCase().replace(/ /g, "-")}`} className="group rounded-2xl bg-white p-6 card-lift flex flex-col">
              <span className="w-14 h-14 rounded-2xl flex items-center justify-center text-white" style={{ background: c }}><Icon className="w-7 h-7" /></span>
              <p className="font-display uppercase text-2xl font-bold text-[#03045E] mt-4">{l}</p>
              <p className="text-sm text-slate-600 mt-1 flex-1">{d}</p>
              <span className="mt-4 text-sm font-bold text-[#0077B6] group-hover:translate-x-1 transition-transform duration-200">Get started →</span>
            </Link>
          ))}
        </div>
        <p className="text-xs text-sky-200/70 mt-6">Source: CDC Drowning Facts, updated Jan. 27, 2026. Annual estimates; daily figures are averages, not a live counter.</p>
      </div>
    </section>
  );
}

function DailyToll() {
  const data = [{ n: "Fatal drownings", v: 11 }, { n: "Nonfatal drownings", v: 22 }];
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <SectionHead eyebrow="The daily toll of drowning" title="This isn't simply a fence campaign." sub="It's a child- and community-safety campaign. Estimated average U.S. fatal and nonfatal drownings per day, based on CDC annual estimates." />
        <div className="mt-8 space-y-2">
          {LAYERS.map((l, i) => <p key={l} className="font-display uppercase text-xl md:text-2xl text-[#03045E]" style={{ opacity: 1 - i * 0.12 }}>{l}</p>)}
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="rounded-3xl bg-white border border-sky-100 p-6 shadow-sm" data-testid="daily-toll-chart">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="n" tick={{ fontSize: 12, fill: "#03045E", fontWeight: 700 }} axisLine={false} tickLine={false} />
                <YAxis ticks={[0, 6, 12, 18, 24]} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#CAF0F8" }} />
                <RBar dataKey="v" radius={[12, 12, 0, 0]}>{data.map((d, i) => <Cell key={d.n} fill={i ? "#00B4D8" : "#03045E"} />)}</RBar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <SourceNote className="mt-3" />
        </div>
      </Reveal>
    </section>
  );
}

function Pillars() {
  return (
    <section className="bg-white py-20" data-testid="home-pillars">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5"><CircleOfProtection /></div>
        <div className="lg:col-span-7">
          <SectionHead eyebrow="Six pillars. One system." title="We don't simply build fences. We build a community drowning-prevention system." />
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {PILLARS.map(([k, p, name, Icon, c, d]) => (
              <Link to={`/programs#${k}`} key={k} className="rounded-2xl border border-slate-100 p-5 card-lift flex gap-4" data-testid={`pillar-card-${k}`}>
                <span className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center text-white" style={{ background: c }}><Icon className="w-5 h-5" /></span>
                <div><p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: c }}>{p}</p><p className="font-display uppercase text-lg text-[#03045E] leading-tight">{name}</p><p className="text-sm text-slate-600 mt-1">{d}</p></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function KnowledgeBand() {
  const steps = ["Before: uncertain around water", "Learning: floating with an instructor", "Developing: water competency", "Education: life-jacket use", "Family: supervision & emergency response", "Community: Safety Day"];
  return (
    <section className="relative water-bg overflow-hidden" data-testid="home-knowledge">
      <img src={IMG.pool} alt="Children learning to swim with kickboards" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <SectionHead light eyebrow="Swim lessons save lives" title="Don't only build a fence around the water. Build knowledge around the child." />
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-10">
          {steps.map((s, i) => (
            <Reveal key={s} delay={i * 0.06}><div className="rounded-2xl bg-white/15 backdrop-blur-xl border border-white/30 p-4 h-full text-white"><p className="font-display text-3xl">{String(i + 1).padStart(2, "0")}</p><p className="text-sm font-semibold mt-2">{s}</p></div></Reveal>
          ))}
        </div>
        <p className="text-sky-100 text-sm mt-6 max-w-2xl">Per the American Academy of Pediatrics' 2026 policy, swim lessons may begin after a child's first birthday, take multiple lessons to build water competency, and never replace close supervision or other layers of protection.</p>
        <CTAButton to="/sponsor?type=child" variant="orange" className="mt-8" testid="home-sponsor-swimmer">Sponsor a Swimmer™</CTAButton>
      </div>
    </section>
  );
}

function MapPreview() {
  const { data = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="home-map">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4">
        <SectionHead eyebrow="Project map • Jacksonville pilot" title="Every pond. Every stage. In public." />
        <CTAButton to="/map" testid="home-view-all-projects">View all projects</CTAButton>
      </div>
      <div className="mt-6"><MapLegend /></div>
      <div className="mt-6 relative">
        <PondMap projects={data} height={420} />
        {data.length === 0 && <div className="absolute inset-x-4 bottom-4 z-[400] rounded-2xl bg-white/95 backdrop-blur p-4 text-sm text-slate-700 shadow" data-testid="map-empty-note">No pond projects are on the map yet. <Link to="/report" className="font-bold text-[#0077B6]">Report the first pond →</Link></div>}
      </div>
    </section>
  );
}

function Challenge() {
  const goals = [["10", "Pond-safety projects"], ["1,000", "Children sponsored"], ["1,000", "Families reached"], ["500", "Caregivers in CPR education"], ["10", "Community Safety Days"], ["50+", "Sponsors"]];
  return (
    <section className="bg-[#03045E] text-white relative grain" data-testid="home-challenge">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <SectionHead light eyebrow="Swim for Rakim™ Jacksonville Challenge" title="10 Ponds • 1,000 Children • One City" sub="These are program goals, not predictions. Every result is tracked publicly." />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-10">
          {goals.map(([v, l]) => <div key={l} className="border-l-2 border-[#FF6B35] pl-4"><p className="font-display text-4xl">{v}</p><p className="text-sky-200 text-sm">{l}</p></div>)}
        </div>
        <CTAButton to="/impact" variant="light" className="mt-10" testid="home-challenge-progress">Track the progress</CTAButton>
      </div>
    </section>
  );
}

function PromiseBand() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-3 gap-5">
      <Link to="/promise" className="md:col-span-2 rounded-3xl sky-bg border border-sky-100 p-8 card-lift" data-testid="home-promise-card">
        <Eyebrow>For kids</Eyebrow>
        <p className="font-display uppercase text-3xl md:text-4xl text-[#03045E] mt-2">Rakim's Water Safety Promise™</p>
        <p className="text-slate-700 mt-3 max-w-lg">Never go near water alone. Always ask an adult. Listen to my swim instructor. Earn a digital badge and certificate.</p>
        <span className="inline-block mt-6 rounded-full bg-[#03045E] text-white px-6 py-3 font-bold uppercase text-sm">Take the Promise →</span>
      </Link>
      <Link to="/events" className="rounded-3xl overflow-hidden relative card-lift min-h-[260px]" data-testid="home-events-card">
        <img src={IMG.training} alt="Water safety training" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#03045E] to-transparent" />
        <div className="absolute bottom-0 p-6 text-white"><p className="font-display uppercase text-2xl">Community Safety Days</p><p className="text-sm text-sky-100">Education • Demonstrations • Resources • Fun</p></div>
      </Link>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <StatsBand />
      <DailyToll />
      <Pillars />
      <KnowledgeBand />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-20" data-testid="home-impact">
        <SectionHead eyebrow="Our community impact" title="Measured. Public. Real." />
        <div className="mt-8"><ImpactGrid /></div>
      </section>
      <MapPreview />
      <Challenge />
      <PromiseBand />
    </div>
  );
}
